import { useState } from "react";
import { toast } from "sonner";
import { postContact } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Mail, Instagram, MapPin, CheckCircle2 } from "lucide-react";
import { HoundNote } from "@/components/HoundNote";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await postContact(form);
      toast.success(res.message || "Thanks for reaching out!");
      setForm({ name: "", email: "", message: "" });
      setSent(true);
    } catch {
      toast.error("Please check your details and try again.");
    } finally {
      setBusy(false);
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-16">
      <div className="text-center mb-14">
        <p className="text-xs uppercase tracking-[0.25em] text-rosegold mb-3">Say hello</p>
        <h1 className="font-display text-4xl sm:text-5xl text-maroon">Get in touch</h1>
        <p className="mt-4 text-inkbrown/70 max-w-lg mx-auto">
          Questions about sizing, pre-orders or just want to share a photo of your hound? We'd love to hear from you.
        </p>
      </div>

      <div className="grid lg:grid-cols-5 gap-10">
        <div className="lg:col-span-2 space-y-6">
          {[
            { icon: Mail, t: "Email", s: "houndoz@outlook.com" },
            { icon: Instagram, t: "Instagram", s: "hound.oz" },
            { icon: MapPin, t: "Studio", s: "Australia" },
          ].map((c) => (
            <div key={c.t} className="flex items-start gap-4 rounded-3xl bg-white p-6 border border-rosegold/15">
              <c.icon className="w-5 h-5 text-rosegold mt-0.5" strokeWidth={1.5} />
              <div>
                <p className="text-inkbrown font-medium">{c.t}</p>
                <p className="text-sm text-inkbrown/60">{c.s}</p>
              </div>
            </div>
          ))}
          <HoundNote
            author="George"
            message="Leave us a message! I'd reply myself, but I haven't quite worked out thumbs yet — a human will get back to you very soon."
            className="max-w-none -rotate-1"
          />
        </div>

        <form onSubmit={submit} data-testid="contact-form" className="lg:col-span-3 rounded-3xl bg-white p-8 border border-rosegold/15 space-y-5">
          {sent ? (
            <div data-testid="contact-success" className="flex flex-col items-center text-center py-10 px-4">
              <CheckCircle2 className="w-14 h-14 text-rosegold" strokeWidth={1.3} />
              <h2 className="font-display text-2xl text-maroon mt-5">Message sent!</h2>
              <p className="text-inkbrown/70 mt-3 max-w-sm">
                Thank you for reaching out — your message has landed safely in our inbox and a human will get back to you very soon.
              </p>
              <button
                type="button"
                onClick={() => setSent(false)}
                data-testid="contact-send-another"
                className="mt-7 h-11 px-7 rounded-full border border-maroon text-maroon hover:bg-maroon hover:text-cream transition-colors text-sm tracking-wide"
              >
                Send another message
              </button>
            </div>
          ) : (
          <>
          <div>
            <Label htmlFor="name" className="text-inkbrown">Name</Label>
            <Input id="name" required value={form.name} onChange={set("name")} data-testid="contact-name"
              className="mt-2 rounded-xl border-secondary focus-visible:ring-maroon" placeholder="Your name" />
          </div>
          <div>
            <Label htmlFor="email" className="text-inkbrown">Email</Label>
            <Input id="email" type="email" required value={form.email} onChange={set("email")} data-testid="contact-email"
              className="mt-2 rounded-xl border-secondary focus-visible:ring-maroon" placeholder="you@email.com" />
          </div>
          <div>
            <Label htmlFor="message" className="text-inkbrown">Message</Label>
            <Textarea id="message" required rows={5} value={form.message} onChange={set("message")} data-testid="contact-message"
              className="mt-2 rounded-xl border-secondary focus-visible:ring-maroon" placeholder="How can we help?" />
          </div>
          <button type="submit" disabled={busy} data-testid="contact-submit"
            className="w-full h-12 rounded-full bg-maroon text-cream hover:bg-maroon-dark transition-transform hover:-translate-y-0.5 disabled:opacity-60">
            {busy ? "Sending…" : "Send message"}
          </button>
          </>
          )}
        </form>
      </div>
    </div>
  );
}
